import { PROD } from "@/data/Environment";
import { Browser } from "@capacitor/browser";
import GoogleLogo from "@/assets/img/icons/google-logo.svg?react";
// import EmailLogo from "@/assets/img/icons/email.svg?react";
import AppleLogo from "@/assets/img/icons/apple.svg?react";
import TwitchLogo from "@/assets/img/icons/twitch.svg?react";
import NotchedBlock from "./NotchedBlock";
import ModalCloseIcon from "@/assets/img/layout/close-modal.svg?react";
import UIListener from "@/pages/home/UIListener";
import { twMerge } from "tailwind-merge";
import social from "@/api/social";

const firstUpperCase = (str: string) =>
{
    if (str.length <= 1)
    {
        return str;
    }

    return str.charAt(0).toUpperCase() + str.slice(1);
};

function SocialLoginButton({
    text,
    icon,
    className,
    onClick,
}: {
    text: string;
    className?: string;
    icon: React.ReactNode;
    onClick: () => void;
})
{
    return (
        <button
            className={twMerge(Wind.style({
                backgroundColor: "bg-[#E8DAC7]",
                color: "text-[#6C3331]",
                fontSize: "text-base",
                lineHeight: "leading-6",
                fontFamily: "font-poppins",
                fontWeight: "font-semibold",
                borderRadius: "rounded-full",
                paddingY: "py-2",
                paddingX: "px-3",
                display: "flex",
                alignItems: "items-center",
                justifyContent: "justify-center",
                gap: "gap-2",
                width: "w-full",
                textAlign: "text-center",
            }).class, className)}
            onClick={onClick}
        >
            {icon}
            <div className="flex-grow">
                {text}
            </div>
        </button>
    );
}

export enum ConnectPlatformType
{
    TWITCH = "twitch",
    DISCORD = "discord"
}

export async function DisconnectPlatform(
    platform: ConnectPlatformType,
    setError?: (values: { title: string, error: string } | null) => void,
    onConnect?: () => void,
)
{
    const token = localStorage.getItem("access_token");

    if (!token)
    {
        Logger.error("No access token found for connecting to platform", platform);

        if (setError)
        {
            setError({
                title: "Connection Error",
                error: `You need to be logged in to disconnect your 
                ${firstUpperCase(platform)} account. Please log in and try again.`,
            });
        }

        return;
    }

    switch (platform)
    {
        // @
        case ConnectPlatformType.TWITCH: {
            const disconnected = await social.profile.disconnectTwitch();

            if (!disconnected)
            {
                Logger.error("Failed to disconnect from Twitch");

                if (setError)
                {
                    setError({
                        title: "Disconnect Error",
                        error: "There was an error disconnecting from Twitch. Please try again."
                    });
                }
            }
            else
            {
                Logger.log("Profile API", "Twitch disconnected successfully.");
                onConnect?.();
            }
        }

            break;
        default:
            // Noop
            break;
    }
}

export function ConnectWithPlatform(
    platform: ConnectPlatformType,
    setError?: (values: { title: string, error: string } | null) => void,
    onConnect?: () => void,
)
{
    const token = localStorage.getItem("access_token");

    if (!token)
    {
        Logger.error("No access token found for connecting to platform", platform);

        if (setError)
        {
            setError({
                title: "Connection Error",
                error: `You need to be logged in to connect your 
                ${firstUpperCase(platform)} account. Please log in and try again.`,
            });
        }

        return;
    }

    if (PROD)
    {
        Browser.open({ url: `https://social.realizemag.com/connect/${platform}?token=${token}` }).catch((e) =>
        {
            Logger.error(`Error opening ${firstUpperCase(platform)} login`, e);

            if (setError)
            {
                setError({
                    title: `${firstUpperCase(platform)} Login Error`,
                    error: `There was an error opening the ${firstUpperCase(platform)} login page. Please try again.`
                });
            }
        });

        Browser.addListener("browserFinished", () =>
        {
            // Get the URL from the event
            onConnect?.();
        }).catch((e) =>
        {
            Logger.error("Error adding browser listener", e);

            if (setError)
            {
                setError({
                    title: `${firstUpperCase(platform)} Login Error`,
                    error: `There was an error opening the ${firstUpperCase(platform)} login page. Please try again.`
                });
            }
        });
    }
    else
    {
        window.open(`http://localhost:3005/connect/${platform}?token=${token}`, "_self");
    }
}

export const loginWithPlatform = (
    platform: "google"|"apple"|"twitch",
    setError?: (values: { title: string, error: string } | null ) => void,
    onLogin?: () => void,
) =>
{
    Logger.log(`Login with ${platform}`);

    // Open http://localhost:3005/auth/google and return the results.
    if (PROD)
    {
        Browser.open({ url: `https://social.realizemag.com/auth/${platform}` }).catch((e) =>
        {
            Logger.error(`Error opening ${firstUpperCase(platform)} login`, e);

            if (setError)
            {
                setError({
                    title: `${firstUpperCase(platform)} Login Error`,
                    error: `There was an error opening the ${firstUpperCase(platform)} login page. Please try again.`
                });
            }
        });

        setTimeout(() =>
        {
            onLogin?.();
        }, 1000); // Wait for the browser to open before adding the listener
    }
    else
    {
        window.open(`http://localhost:3005/auth/${platform}`, "_self");
    }
};

export function LoginButtons({setError, onLogin}: {
    setError?: (values: { title: string, error: string } | null) => void;
    onLogin?: () => void;
})
{
    return (
        <div
            className={Wind.style({
                display: "flex",
                flexDirection: "flex-col",
                gap: "gap-2",
                width: "w-full",
                marginBottom: "mb-4",
            }).class}
        >
            {/* <SocialLoginButton
                text="Continue with Email/Password"
                icon={<EmailLogo className="w-9 h-9" />}
                onClick={() =>
                {
                    Logger.log("Login with Email");
                }
                }
            /> */}
            <SocialLoginButton
                text="Continue with Google"
                icon={<GoogleLogo className="w-9 h-9" />}
                onClick={() =>
                {
                    loginWithPlatform("google", setError, onLogin);
                }}
            />
            <SocialLoginButton
                text="Continue with Apple"
                icon={<AppleLogo className="w-9 h-9" />}
                onClick={() =>
                {
                    loginWithPlatform("apple", setError, onLogin);
                }}
            />
            <SocialLoginButton
                text="Continue with Twitch"
                icon={<TwitchLogo className="w-9 h-9" />}
                className="bg-[#9146ff] text-white"
                onClick={() =>
                {
                    loginWithPlatform("twitch", setError, onLogin);
                }}
            />
        </div>
    );
}

export default function PopUpLogin({
    onClose,
}: {
    onClose?: () => void;
})
{
    return (
        <div
            className={Wind.style({
                position: "fixed",
                width: "w-full",
                height: "h-full",
                inset: "inset-0",
                backdropBlur: "backdrop-blur-md",
                backgroundColor: "bg-black/50",
                color: "text-white",
                display: "flex",
                flexDirection: "flex-col",
                alignItems: "items-center",
                justifyContent: "justify-center",
                paddingX: "px-4",
                zIndex: "z-[1000]",
            }).class}
        >
            <div
                className={Wind.style({
                    position: "relative",
                    filterDropShadow: "drop-shadow-[0_4px_6px_rgba(0,0,0,0.1)]",
                }).class}
            >
                <NotchedBlock
                    className={Wind.style({
                        paddingTop: "pt-10",
                        position: "relative",
                    }).class}
                >

                    <h1
                        className={Wind.style({
                            fontFamily: "font-poppins",
                            fontSize: "text-3xl",
                            fontWeight: "font-bold",
                            lineHeight: "leading-9",
                            letterSpacing: "tracking-normal",
                            color: "text-[#6C3331]",
                            textAlign: "text-center",
                            marginBottom: "mb-12",
                        }).class}
                    >
                        Login to Realize
                    </h1>
                    <LoginButtons
                        onLogin={() =>
                        {
                            if (onClose)
                            {
                                onClose();
                            }
                            else
                            {
                                UIListener.trigger("closePopUpLogin", {});
                            }
                        }}
                    />
                    <p
                        className={Wind.style({
                            fontFamily: "font-poppins",
                            fontSize: "text-sm",
                            textAlign: "text-center",
                            textWrap: "text-balance",
                            color: "text-[#6C3331CC]",
                            paddingBottom: "pb-6",
                        }).class}
                    >
                        By continuing,
                        you are signing up or logging in and agree to Realize&apos;s
                        Terms of Use and Privacy Policy.
                    </p>
                </NotchedBlock>
                <button
                    onClick={(e) =>
                    {
                        e.stopPropagation();

                        if (onClose)
                        {
                            onClose();
                        }
                        else
                        {
                            UIListener.trigger("closePopUpLogin", {});
                        }
                    }}
                    className={Wind.style({
                        position: "absolute",
                        top: "top-0",
                        right: "right-0",
                        transformTranslateX: "-translate-x-1/4",
                        transformTranslateY: "-translate-y-1/4",
                    }).class}
                >
                    <ModalCloseIcon className="w-9 h-9" />
                </button>
            </div>
        </div>
    );
}
